
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FaSearch, FaFileExcel, FaFilePdf, FaTimes } from "react-icons/fa";
import { format, subDays } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import "./PanicReport.css";

const API_BASE_URL = "https://api.yellowmatrix.co.in";

const useAvailableVehiclesQuery = () => {
  const fetchAvailableVehicles = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response = await fetch(`${API_BASE_URL}/vehicles`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("HTTP error! status: " + response.status);
    }

    const data = await response.json();
    return data.map((v) => v.VehicleID);
  };

  return useQuery({
    queryKey: ["availableVehicles"],
    queryFn: fetchAvailableVehicles,
    staleTime: Infinity,
  });
};

const PanicReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [dateRange, setDateRange] = useState("Last Day");
  const [startDate, setStartDate] = useState(
    format(subDays(new Date(), 1), "yyyy-MM-dd")
  );
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  
  // --- Pagination States (Mirrored from Reference) ---
  const [page, setPage] = useState(1);
  const perPage = 25;
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [reportGenerated, setReportGenerated] = useState(false);

  const searchInputRef = useRef(null);
  const hasData = reportData.length > 0;

  const removeVehicle = (vehicle) => {
    setSelectedVehicles(selectedVehicles.filter((v) => v !== vehicle));
  };

  const formatDateForAPI = (date) => {
    const [year, month, day] = date.split("-");
    return `${day}/${month}/${year}`;
  };

  // --- Click Handler matching Reference ---
  const handleGenerateClick = () => {
    setPage(1);
    handleGenerateReport();
    setReportGenerated(true);
  };

  // --- Auto Fetch Effect Tracking Page Changes ---
  useEffect(() => {
    if (reportGenerated) {
      handleGenerateReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, reportGenerated]);

  const handleGenerateReport = async () => {
    try {
      setLoadingReport(true);
      const token = localStorage.getItem("token");
      const vehicleIds = selectedVehicles.join(",");

      const response = await fetch(
        `${API_BASE_URL}/panic-report?vehicle_id=${vehicleIds}&start_date=${formatDateForAPI(startDate)}&end_date=${formatDateForAPI(endDate)}&page=${page}&per_page=${perPage}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.Error || "Failed to fetch report");
      }

      setReportData(data.data || []);
      setTotalPages(data.pages || 0);
      setTotalRecords(data.total || 0);
    } catch (err) {
      console.error("Panic Report Error:", err);
      alert(err.message);
    } finally {
      setLoadingReport(false);
    }
  };

  const handleDownload = async (fileType) => {
    try {
      const token = localStorage.getItem("token");
      const vehicleIds = selectedVehicles.join(",");

      const response = await fetch(
        `${API_BASE_URL}/panic-report?vehicle_id=${vehicleIds}&start_date=${formatDateForAPI(startDate)}&end_date=${formatDateForAPI(endDate)}&download=${fileType}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileType === "xlsx" ? "panic_report.xlsx" : "panic_report.pdf";

      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Failed to download file");
    }
  };

  const {
    data: availableVehicles = [],
    isLoading: fetchingVehicles,
    error: vehicleFetchError,
  } = useAvailableVehiclesQuery();

  useEffect(() => {
    const filtered =
      searchInput.length > 0
        ? availableVehicles.filter((vehicle) =>
            vehicle.toLowerCase().includes(searchInput.toLowerCase())
          )
        : availableVehicles;

    setFilteredSuggestions(filtered);
  }, [searchInput, availableVehicles]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
    setShowSuggestions(true);
  };

  const onSuggestionClick = (vehicleId) => {
    setSearchInput("");
    setShowSuggestions(false);

    if (!selectedVehicles.includes(vehicleId)) {
      setSelectedVehicles([...selectedVehicles, vehicleId]);
    }
  };

  const handleDateRangeChange = (e) => {
    const value = e.target.value;
    setDateRange(value);

    const today = new Date();
    const dateMap = {
      "Last Day": 1,
      "Last 7 Days": 7,
      "Last 30 Days": 30,
    };

    if (dateMap[value] !== undefined) {
      setStartDate(format(subDays(today, dateMap[value]), "yyyy-MM-dd"));
      setEndDate(format(today, "yyyy-MM-dd"));
    }
  };

  const handleReset = () => {
    setSearchInput("");
    setSelectedVehicles([]);
    setDateRange("Last Day");
    setStartDate(format(subDays(new Date(), 1), "yyyy-MM-dd"));
    setEndDate(format(new Date(), "yyyy-MM-dd"));
    setReportData([]);
    setPage(1);
    setTotalPages(0);
    setTotalRecords(0);
    setReportGenerated(false);
  };

  return (
    <div className="panic-report-container">
      <h1 className="report-title">Panic Report</h1>
      <hr className="panic-report-line" width="100%" />
      
      <div className="controls-container">
        <div className="search-container-panic" ref={searchInputRef}>
          <FaSearch className="search-icon-panic" />

          <input
            type="text"
            placeholder="Search Vehicle ID..."
            className="search-input-panic"
            value={searchInput}
            onChange={handleSearchInputChange}
            onFocus={() => {
              setFilteredSuggestions(availableVehicles);
              setShowSuggestions(true);
            }}
          />

          {showSuggestions && filteredSuggestions.length > 0 && (
            <ul className="suggestions-list-alert">
              {filteredSuggestions.map((vehicleId, index) => (
                <li
                  key={index}
                  className="suggestion-item-alert"
                  onClick={() => onSuggestionClick(vehicleId)}
                >
                  {vehicleId}
                </li>
              ))}
            </ul>
          )}

          {fetchingVehicles && (
            <div className="loading-suggestions-alert">Loading vehicles...</div>
          )}

          {vehicleFetchError && (
            <div className="error-message-small-alert">
              {vehicleFetchError.message}
            </div>
          )}
        </div>

        <div className="date-range-container">
          <label className="control-label">Date Range</label>
          <select
            value={dateRange}
            onChange={handleDateRangeChange}
            className="date-range-select"
          >
            <option>Last Day</option>
            <option>Last 7 Days</option>
            <option>Last 30 Days</option>
            <option>Custom</option>
          </select>
        </div>

        <div className="date-input-container">
          <label className="control-label">Start Date:</label>
          <input
            type="date"
            className="date-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={dateRange !== "Custom"}
          />
        </div>

        <div className="date-input-container">
          <label className="control-label">End Date:</label>
          <input
            type="date"
            className="date-input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={dateRange !== "Custom"}
          />
        </div>

        <button
          className={`generate-report-btn ${
            selectedVehicles.length === 0 ? "disabled-btn" : ""
          }`}
          onClick={handleGenerateClick}
          disabled={selectedVehicles.length === 0 || loadingReport}
        >
          {loadingReport ? "LOADING..." : "GENERATE REPORT"}
        </button>

        <button className="action-btn-panic reset-btn" onClick={handleReset}>
          RESET
        </button>
        
        <Link to="/Report">
          <button className="action-btn-panic back-btn">
            BACK TO ALL REPORTS
          </button>
        </Link>
        
        <FaFileExcel
          className={`excel-icon file-icon ${
            loadingReport || !hasData ? "disabled-icon" : ""
          }`}
          title={hasData ? "Export to Excel" : "No data available to export"}
          onClick={() => hasData && handleDownload("xlsx")}
        />

        <FaFilePdf
          className={`pdf-icon file-icon ${
            loadingReport || !hasData ? "disabled-icon" : ""
          }`}
          title={hasData ? "Export to PDF" : "No data available to export"}
          onClick={() => hasData && handleDownload("pdf")}
        />
      </div>

      <div className="selected-vehicles-container">
        {selectedVehicles.length > 0 ? (
          selectedVehicles.map((vehicle, idx) => (
            <span key={idx} className="vehicle-tag">
              {vehicle}
              <FaTimes
                className="remove-icon"
                onClick={() => removeVehicle(vehicle)}
                title="Remove vehicle"
              />
            </span>
          ))
        ) : (
          <div className="no-vehicles-selected">No vehicles selected</div>
        )}
      </div>

      <div className="table-container-panic">
        {/* Summary Metric Strip (Matches Reference File) */}
        {reportData.length > 0 && (
          <div className="report-summary-bar">
            Showing <strong>{reportData.length}</strong> of{" "}
            <strong>{totalRecords}</strong> records | Page{" "}
            <strong>{page}</strong> of <strong>{totalPages}</strong>
          </div>
        )}

        <table className="report-table-panic">
          <thead className="table-header">
            <tr>
              <th className="table-heading">Date</th>
              <th className="table-heading">Vehicle Number</th>
              <th className="table-heading">Device ID</th>
              <th className="table-heading">Group</th>
              <th className="table-heading">Alert Start Time</th>
              <th className="table-heading">Alert Address</th>
              <th className="table-heading">Alert Acknowledgement Time</th>
              <th className="table-heading">Current Address</th>
              <th className="table-heading">Current Speed</th>
              <th className="table-heading">Last Time Tracked</th>
            </tr>
          </thead>
          <tbody>
            {loadingReport ? (
              <tr>
                <td colSpan="10">Loading...</td>
              </tr>
            ) : reportData.length > 0 ? (
              reportData.map((row, index) => (
                <tr key={index}>
                  <td>{row.Date}</td>
                  <td>{row["Vehicle number"]}</td>
                  <td>{row["Device ID"]}</td>
                  <td>{row.Group}</td>
                  <td>{row["Alert start time"]}</td>
                  <td>{row["Alert address"]}</td>
                  <td>{row["Alert Acknowledgement time"] || "N/A"}</td>
                  <td>{row["Current address"]}</td>
                  <td>{row["Current speed"]}</td>
                  <td>{row["Last time tracked"]}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10">No Data Found</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Modular Pagination Drawer Interface (Matches Reference File) */}
        {totalPages > 1 && (
          <div className="pagination-container-alert">
            <button
              className="pagination-btn-alert"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loadingReport}
            >
              ← Prev
            </button>

            <span className="pagination-info-alert">
              Page {page} / {totalPages}
            </span>

            <button
              className="pagination-btn-alert"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loadingReport}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PanicReport;